import React, { useContext, useEffect, useState } from "react";
import axios from "../../../axios";
import "./createTimeTable.css";
import { useNavigate } from "react-router-dom";
import DuplicateErr from "./duplicateErr";
import { IP_BE } from "../../../internalServer";
import { SubStdSelContext } from "../../../SubStdSelContext";
import NotUploadTimetable from "./notUploadTimetable";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  LinearProgress,
  Typography,
  Paper,
  Stack,
  Fade,
  Grow,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  CloudUpload,
  InsertDriveFile,
  CheckCircle,
  ErrorOutline,
  Close,
  Publish,
  Refresh,
  Download,
  DeleteOutline,
} from "@mui/icons-material";

function CreateTimeTable(props) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [timetableId, setTimeTableId] = useState(null);
  const [status, setStatus] = useState("idle");
  const { secSelectedOption, stdSelectedOption } = useContext(SubStdSelContext);

  let schoolId = JSON.parse(localStorage.getItem("schoolId"));
  let timeTableSessionId = JSON.parse(
    localStorage.getItem("timeTableSessionId"),
  );

  const acdId = JSON.parse(localStorage.getItem("academicYearId"));

  const [errorMsg, setErrorMsg] = useState(null);
  const navigate = useNavigate();

  const handleFileUpload = async () => {
    try {
      if (!timeTableSessionId) {
        toast.error(
          "These are no allocations set for your login. Please contact school admin for the assistance.",
        );
        return;
      }

      if (selectedFile.size > 4000000) {
        toast.error("The file size should be less than 4MB.");
        return;
      }

      setStatus("uploading");
      setErrorMsg(null);

      const formData = new FormData();
      formData.append("uploadTimetable", selectedFile);

      await axios.post(
        `/api/v1/timetable/upload/schoolId/${schoolId}/timetableSessionId/${timeTableSessionId}?standard_id=${stdSelectedOption}&section_id=${secSelectedOption}&academic_year_id=${acdId}`,
        formData,
        {
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100,
            );
            setUploadProgress(progress);
          },
        },
      );

      setStatus("success");
    } catch (error) {
      setStatus("error");
      setErrorMsg(
        error?.response?.data?.message ||
          "Something went wrong. Please try again.",
      );

      if (error?.response?.data?.data?.duplicateTimeTable_id) {
        setTimeTableId(error?.response?.data?.data?.duplicateTimeTable_id);
      }
    }
  };

  useEffect(() => {
    if (selectedFile) {
      handleFileUpload();
    }
  }, [selectedFile]);

  const handlePublish = () => {
    if (status === "success") {
      toast.success("Timetable uploaded successfully");
      navigate("/lactureManagement");
    }
  };

  const handleDownload = (template) => {
    const downloadAnchor = document.createElement("a");
    downloadAnchor.style.display = "none";
    document.body.appendChild(downloadAnchor);
    downloadAnchor.href = template;
    downloadAnchor.setAttribute("download", "Timetable_template.xlsx");
    downloadAnchor.click();
    document.body.removeChild(downloadAnchor);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const resetUpload = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    setStatus("idle");
    setErrorMsg(null);
  };

  // 🔥 COMPACT CARD STYLES — maxHeight 340 ke hisaab se
  const cardStyles = {
    borderRadius: 3,
    minHeight: 300,
    maxHeight: 340,
    mx: "auto",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  };

  return (
    <Box
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      sx={{ width: "100%", py: 2 }}
    >
      {/* ═══════════════════════════════════════
          CASE 1: IDLE — Upload Zone
      ═══════════════════════════════════════ */}
      {status === "idle" && (
        <Fade in={true} timeout={500}>
          <Box>
            <Paper
              elevation={0}
              sx={{
                ...cardStyles,
                border: "2px dashed",
                borderColor: "#e0e0e0",
                bgcolor: "#fafafa",
                p: 3,
                textAlign: "center",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: "#f77f00",
                  bgcolor: "#fff8e1",
                  boxShadow: "0 8px 32px rgba(247,127,0,0.08)",
                },
              }}
            >
              <Stack spacing={2} alignItems="center" justifyContent="center">
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    bgcolor: "#fff3e0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CloudUpload sx={{ fontSize: 28, color: "#f77f00" }} />
                </Box>

                <Box>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    color="#2d3436"
                    gutterBottom
                  >
                    Upload Timetable
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Drag & drop your Excel file here
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    .xls, .xlsx (Max 4MB)
                  </Typography>
                </Box>

                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.disabled"
                >
                  — OR —
                </Typography>

                <input
                  hidden
                  type="file"
                  accept=".xls,.xlsx"
                  id="attachFile"
                  onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                />
                <label htmlFor="attachFile">
                  <Button
                    variant="contained"
                    component="span"
                    size="medium"
                    startIcon={<CloudUpload />}
                    sx={{
                      borderRadius: 2,
                      px: 4,
                      py: 1,
                      fontWeight: 700,
                      textTransform: "none",
                      background:
                        "linear-gradient(135deg, #f77f00 0%, #fb8c00 100%)",
                      boxShadow: "0 4px 16px rgba(247,127,0,0.3)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #e65100 0%, #f77f00 100%)",
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    Browse File
                  </Button>
                </label>
              </Stack>
            </Paper>

            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Button
                onClick={() =>
                  handleDownload(
                    `${IP_BE}/public/upload/timetables/template/timetable_template.xlsx`,
                  )
                }
                startIcon={<Download />}
                size="small"
                sx={{
                  color: "#f77f00",
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "0.85rem",
                  "&:hover": { bgcolor: "#fff3e0" },
                }}
              >
                Download Template
              </Button>
            </Box>
          </Box>
        </Fade>
      )}

      {/* ═══════════════════════════════════════
          CASE 2: UPLOADING — Progress
      ═══════════════════════════════════════ */}
      {status === "uploading" && (
        <Grow in={true} timeout={400}>
          <Paper
            elevation={2}
            sx={{
              ...cardStyles,
              p: 3,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack
              spacing={3}
              alignItems="center"
              justifyContent="center"
              width="100%"
            >
              <Box sx={{ width: "100%" }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 0.5 }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="text.secondary"
                  >
                    Uploading...
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={800}
                    color="primary.main"
                  >
                    {uploadProgress}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: "#fff3e0",
                    "& .MuiLinearProgress-bar": {
                      bgcolor: "#f77f00",
                      borderRadius: 3,
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: "block", textAlign: "center" }}
                >
                  Creation can take 1–2 min. Please wait...
                </Typography>
              </Box>

              <Paper
                elevation={0}
                sx={{
                  width: "100%",
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "#fafafa",
                  border: "1px solid #eeeeee",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    bgcolor: "#e8f5e9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <InsertDriveFile sx={{ color: "#4caf50", fontSize: 18 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    noWrap
                    fontSize="0.85rem"
                  >
                    {selectedFile?.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontSize="0.75rem"
                  >
                    {(selectedFile?.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                </Box>
                <Chip
                  label="Uploading"
                  size="small"
                  sx={{
                    bgcolor: "#fff3e0",
                    color: "#e65100",
                    fontWeight: 700,
                    fontSize: "0.7rem",
                    height: 22,
                    borderRadius: 1,
                  }}
                />
              </Paper>
            </Stack>
          </Paper>
        </Grow>
      )}

      {/* ═══════════════════════════════════════
          CASE 3: SUCCESS — Done
      ═══════════════════════════════════════ */}
      {status === "success" && (
        <Grow in={true} timeout={400}>
          <Paper
            elevation={2}
            sx={{
              ...cardStyles,
              p: 3,
              textAlign: "center",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack spacing={2.5} alignItems="center" justifyContent="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  bgcolor: "#e8f5e9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 16px rgba(76,175,80,0.2)",
                }}
              >
                <CheckCircle sx={{ fontSize: 32, color: "#4caf50" }} />
              </Box>

              <Box>
                <Typography variant="h6" fontWeight={800} color="#2d3436">
                  Upload Complete!
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Your timetable has been uploaded successfully
                </Typography>
              </Box>

              <Paper
                elevation={0}
                sx={{
                  width: "100%",
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "#fafafa",
                  border: "1px solid #eeeeee",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    bgcolor: "#e8f5e9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <InsertDriveFile sx={{ color: "#4caf50", fontSize: 18 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    noWrap
                    fontSize="0.85rem"
                  >
                    {selectedFile?.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontSize="0.75rem"
                  >
                    {(selectedFile?.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                </Box>
                <CheckCircle sx={{ color: "#4caf50", fontSize: 20 }} />
              </Paper>

              <Button
                variant="contained"
                size="medium"
                onClick={handlePublish}
                endIcon={<Publish />}
                sx={{
                  px: 4,
                  py: 1,
                  borderRadius: 2,
                  fontWeight: 700,
                  textTransform: "none",
                  background:
                    "linear-gradient(135deg, #f77f00 0%, #fb8c00 100%)",
                  boxShadow: "0 4px 16px rgba(247,127,0,0.35)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #e65100 0%, #f77f00 100%)",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                Publish Timetable
              </Button>
            </Stack>
          </Paper>
        </Grow>
      )}

      {/* ═══════════════════════════════════════
          CASE 4: ERROR — Retry (SAME COMPACT BOX)
      ═══════════════════════════════════════ */}
      {status === "error" && (
        <Grow in={true} timeout={400}>
          <Paper
            elevation={2}
            sx={{
              ...cardStyles,
              p: 3,
              textAlign: "center",
              border: "1px solid",
              borderColor: "#ffcdd2",
              bgcolor: "#fffafa",
            }}
          >
            <Stack
              spacing={2.5}
              alignItems="center"
              justifyContent="center"
              width="100%"
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  bgcolor: "#ffebee",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 16px rgba(211,47,47,0.15)",
                }}
              >
                <ErrorOutline sx={{ fontSize: 32, color: "#d32f2f" }} />
              </Box>

              <Box>
                <Typography variant="h6" fontWeight={800} color="#2d3436">
                  Upload Failed
                </Typography>
              </Box>

              <Box
                sx={{
                  width: "100%",
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "#ffebee",
                  border: "1px dashed #ef9a9a",
                }}
              >
                <Typography
                  variant="caption"
                  color="#c62828"
                  fontWeight={700}
                  sx={{ lineHeight: 1.6 }}
                  display="block"
                >
                  {errorMsg ||
                    "Something went wrong. Please check your file and try again."}
                </Typography>
              </Box>

              <Paper
                elevation={0}
                sx={{
                  width: "100%",
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "#fafafa",
                  border: "1px solid #eeeeee",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    bgcolor: "#ffebee",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <InsertDriveFile sx={{ color: "#d32f2f", fontSize: 18 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    noWrap
                    fontSize="0.85rem"
                  >
                    {selectedFile?.name}
                  </Typography>
                </Box>
                <Tooltip title="Remove file">
                  <IconButton
                    onClick={resetUpload}
                    size="small"
                    sx={{ color: "#d32f2f", "&:hover": { bgcolor: "#ffebee" } }}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Paper>

              <Stack direction="row" spacing={1.5} sx={{ width: "100%" }}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="medium"
                  onClick={resetUpload}
                  startIcon={<Close />}
                  sx={{
                    borderRadius: 2,
                    py: 1,
                    fontWeight: 700,
                    textTransform: "none",
                    borderColor: "#e0e0e0",
                    color: "#616161",
                    fontSize: "0.85rem",
                    "&:hover": { borderColor: "#bdbdbd", bgcolor: "#f5f5f5" },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  size="medium"
                  onClick={() => {
                    setStatus("idle");
                    setSelectedFile(null);
                  }}
                  startIcon={<Refresh />}
                  sx={{
                    borderRadius: 2,
                    py: 1,
                    fontWeight: 700,
                    textTransform: "none",
                    fontSize: "0.85rem",
                    background:
                      "linear-gradient(135deg, #f77f00 0%, #fb8c00 100%)",
                    boxShadow: "0 4px 16px rgba(247,127,0,0.3)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #e65100 0%, #f77f00 100%)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  Upload Again
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Grow>
      )}

      {/* ═══════════════════════════════════════
          CASE 5 & 6: Duplicate / Not Uploadable
      ═══════════════════════════════════════ */}
      {status === "duplicate" && (
        <DuplicateErr props={props} ID={timetableId} />
      )}
      {status === "notUploadable" && <NotUploadTimetable props={props} />}
    </Box>
  );
}

export default CreateTimeTable;
